import { Fragment } from 'react'

import type { Schemas } from '@npc/api-client'

import {
  AlignLeft,
  Image as ImageIcon,
  Link2,
  MessageSquare,
  Mic,
} from 'lucide-react'

import { AiAccessBadge, ScopeLabel } from './scope'
import { cn } from '../../components/cn'
import { hhmm, daysBetween } from '../../app/format'
import { t } from '../../copy'

/**
 * 备忘录列表项 `MemoItem`（SPEC-components §3，S01 桌面 / M01 移动）。
 *
 * **正文是视觉第一优先级**：两行截断，不显示 AI 编造的标题——
 * 正文本身就是标题。右列是作用域标签与 AI 状态。
 *
 * 桌面（≥700px）：一行式，左侧来源图标，右列徽标；移动（<700px）：
 * 390px 放不下右列，改成三层——正文两行 → 来源/时间/事实标签 →
 * 作用域徽标 + AI 状态（高度 ≥30px，--l1 分隔线隔开）。
 *
 * 来源是**事实**不是装饰：手写/语音/链接/附件/来自 Lulu 五种，
 * 由数据判出来（链接=正文以网址开头），不由 AI 编。
 *
 * 无正文的语音条目写「（语音 1 分 08 秒 · 还没有文字）」——
 * 不生成空字符串冒充成功（文案立场第 3 条）。
 */
/** 第一张图片附件（R2-2）：列表/平铺的缩略图来源。锁着的条目没有附件，天然不渲染。 */
export function firstImage(memo: Schemas['Memo']): Schemas['MemoAttachment'] | null {
  return (memo.attachments ?? []).find((a) => a.mime.startsWith('image/')) ?? null
}

/** 搜索命中高亮（R1-2）：命中的词块上加一层主色底，不只过滤。 */
export function Highlight(props: { text: string; query: string }) {
  const q = props.query.trim()
  if (!q) return <>{props.text}</>
  const lower = props.text.toLowerCase()
  const needle = q.toLowerCase()
  const parts: Array<{ text: string; hit: boolean }> = []
  let i = 0
  while (i < props.text.length) {
    const at = lower.indexOf(needle, i)
    if (at === -1) {
      parts.push({ text: props.text.slice(i), hit: false })
      break
    }
    if (at > i) parts.push({ text: props.text.slice(i, at), hit: false })
    parts.push({ text: props.text.slice(at, at + needle.length), hit: true })
    i = at + needle.length
  }
  return (
    <>
      {parts.map((part, index) =>
        part.hit ? (
          <mark
            key={index}
            data-testid="memo-hit"
            className="rounded-[2px] bg-m-pri/30 px-[1px] text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            {part.text}
          </mark>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </>
  )
}

/** 锁着的条目正文占位：服务器根本没把正文发过来，这不是前端遮一下。 */
export function lockedPlaceholder(): string {
  return t('memo.lock.placeholder')
}

export function MemoItem(props: {
  memo: Schemas['Memo']
  /** 作用域标签的显示名（项目页传项目名，全局页传「杂项」）。 */
  scopeName: string
  selected?: boolean
  dim?: boolean
  /** 搜索词：命中处高亮（R1-2）。 */
  query?: string
  onOpen: (memoId: string) => void
  'data-testid'?: string
}) {
  const { memo } = props
  const locked = memo.has_password
  const voiceNoText = !locked && !memo.body && memo.audio
  const source = memoSource(memo)
  const images = (memo.attachments ?? []).filter((a) => a.mime.startsWith('image/')).length
  const files = (memo.attachments ?? []).length - images

  return (
    <li
      className={cn(
        'm-0 list-none border-b border-m-line1 last:border-b-0',
        // 移动端（M01）：一层一张卡，不再共享外框的行分隔
        'max-[700px]:rounded-npc-card max-[700px]:border max-[700px]:border-m-line2 max-[700px]:bg-m-bg1',
      )}
    >
      <button
        type="button"
        onClick={() => props.onOpen(memo.id)}
        data-testid={props['data-testid'] ?? 'memo-item'}
        aria-current={props.selected ? 'true' : undefined}
        className={cn(
          'flex w-full cursor-pointer items-start gap-[11px] px-[13px] py-[12px] text-left font-[inherit] transition-colors',
          props.selected ? 'bg-m-bg2' : 'bg-transparent hover:bg-m-line1',
        )}
      >
        {/* 来源图标（桌面）：形状区分来源，不靠颜色 */}
        <span
          className={cn(
            'mt-[2px] flex h-[15px] w-[15px] shrink-0 items-center justify-center text-m-t4',
            'max-[700px]:hidden',
          )}
          aria-hidden
        >
          <SourceIcon source={source} size={13} />
        </span>

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'm-0 line-clamp-2 text-[18px] leading-[1.7] max-[700px]:text-[16px] max-[700px]:leading-[1.65]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
              props.dim || voiceNoText ? 'text-m-t3' : 'text-m-t1',
            )}
            data-testid="memo-item-body"
          >
            {locked
              ? lockedPlaceholder()
              : voiceNoText
                ? t('memo.list.voice.no_text', { duration: clockCN(memo.audio?.duration_ms ?? 0) })
                : <Highlight text={memo.body} query={props.query ?? ''} />}
          </p>

          {/* 来源 / 时间 / 事实标签（事实来自数据：真实时长、附件个数） */}
          <p className="m-0 mt-[6px] flex flex-wrap items-center gap-x-[9px] gap-y-[2px] max-[700px]:mt-[8px]">
            <span className="flex items-center gap-[5px] text-[11px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <span className="max-[700px]:inline" aria-hidden>
                <SourceIcon source={source} size={12} />
              </span>
              {sourceLabel(source)}
            </span>
            <span className="font-npc-mono text-[11px] text-m-t4" data-testid="memo-item-when"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {whenCN(memo.updated_at ?? memo.created_at)}
            </span>
            {memo.audio ? (
              <Fact tone="n" label={clockCN(memo.audio.duration_ms)} />
            ) : null}
            {images > 0 ? <Fact tone="n" label={t('memo.list.fact.images', { n: String(images) })} /> : null}
            {files > 0 ? <Fact tone="n" label={t('memo.list.fact.files', { n: String(files) })} /> : null}
          </p>

          {/* 移动端第三层（M01）：作用域 + AI，与上面用 --l1 隔开 */}
          <div className="mt-[9px] hidden items-center gap-[8px] border-t border-m-line1 pt-[9px] max-[700px]:flex">
            <ScopeLabel kind={memo.project_id ? { project: props.scopeName } : 'misc'} />
            <span className="min-w-0 flex-1">
              <AiAccessBadge access={memo.ai_access} className="text-[11.5px]" /> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            </span>
          </div>
        </div>

        {/* 桌面右列（S01）：图片缩略图（R2-2）+ 作用域徽标 + AI 状态 */}
        <div className="flex shrink-0 flex-col items-end gap-[6px] pt-[1px] max-[700px]:hidden">
          {firstImage(memo) ? (
            <img
              src={firstImage(memo)!.url}
              alt={firstImage(memo)!.name}
              loading="lazy"
              data-testid="memo-item-thumb"
              className="h-[40px] w-[40px] rounded-[6px] border border-m-line2 object-cover"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            />
          ) : null}
          <ScopeLabel kind={memo.project_id ? { project: props.scopeName } : 'misc'} />
          <AiAccessBadge access={memo.ai_access} data-testid="memo-item-ai" />
        </div>
      </button>
    </li>
  )
}

/** 事实标签：小圆点 + 文案（SPEC-components §3 的 facts）。 */
function Fact(props: { tone: 'n' | 'g' | 'w'; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[4px] text-[14px]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        props.tone === 'g' ? 'text-m-ok-t' : props.tone === 'w' ? 'text-m-wn-t' : 'text-m-t3',
      )}
    >
      <span
        className={cn(
          'h-[5px] w-[5px] shrink-0 rounded-full',
          props.tone === 'g' ? 'bg-m-ok' : props.tone === 'w' ? 'bg-m-wn' : 'bg-m-t4',
        )}
        aria-hidden
      />
      {props.label}
    </span>
  )
}

/**
 * 平铺卡片（记事本式卡片墙，用户要的第二种显示方式）。
 *
 * 与单列 MemoItem 同一份数据、同一个点击行为（开详情抽屉），差别
 * 只在排版：卡片更方、正文放开到六行——像便签墙一样一眼扫多条。
 * 单列列表仍是默认（设计稿的规矩），平铺由用户自己切、选择记在本机。
 */
export function MemoTile(props: {
  memo: Schemas['Memo']
  scopeName: string
  selected?: boolean
  query?: string
  onOpen: (memoId: string) => void
  'data-testid'?: string
}) {
  const { memo } = props
  const locked = memo.has_password
  const voiceNoText = !locked && !memo.body && memo.audio
  const source = memoSource(memo)
  const images = (memo.attachments ?? []).filter((a) => a.mime.startsWith('image/')).length
  const files = (memo.attachments ?? []).length - images

  return (
    <li className="m-0 list-none" data-testid={props['data-testid'] ?? 'memo-tile-item'}>
      <button
        type="button"
        onClick={() => props.onOpen(memo.id)}
        aria-current={props.selected ? 'true' : undefined}
        className={cn(
          'flex h-full min-h-[120px] w-full cursor-pointer flex-col gap-[8px] rounded-npc-card border p-[11px] text-left font-[inherit] transition-colors',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          props.selected ? 'border-m-line3 bg-m-bg2' : 'border-m-line2 bg-m-bg1 hover:bg-m-bg2',
        )}
      >
        {/* 正文放开六行：平铺的价值就是一眼看到更多内容 */}
        <p
          className={cn(
            'm-0 line-clamp-6 text-[13px] leading-[1.6]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            voiceNoText ? 'text-m-t3' : 'text-m-t1',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          )}
          data-testid="memo-tile-body"
        >
          {locked
            ? lockedPlaceholder()
            : voiceNoText
              ? t('memo.list.voice.no_text', { duration: clockCN(memo.audio?.duration_ms ?? 0) })
              : <Highlight text={memo.body} query={props.query ?? ''} />}
        </p>

        {/* 图片缩略图（R2-2）：有图先给一张 64px 的，扫一眼知内容 */}
        {firstImage(memo) ? (
          <img
            src={firstImage(memo)!.url}
            alt={firstImage(memo)!.name}
            loading="lazy"
            data-testid="memo-tile-thumb"
            className="h-[64px] w-full rounded-[7px] border border-m-line2 object-cover"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          />
        ) : null}

        {/* 卡脚：作用域 + AI 一行，来源/时间/事实标签一行 */}
        <div className="mt-auto flex min-w-0 flex-col gap-[5px] border-t border-m-line1 pt-[8px]">
          <div className="flex items-center gap-[6px] overflow-hidden">
            <ScopeLabel kind={memo.project_id ? { project: props.scopeName } : 'misc'} />
            <span className="min-w-0 truncate">
              <AiAccessBadge access={memo.ai_access} className="text-[11px]" /> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            </span>
          </div>
          <p className="m-0 flex flex-wrap items-center gap-x-[8px] gap-y-[2px]">
            <span className="flex items-center gap-[4px] text-[11px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <SourceIcon source={source} size={12} />
              {sourceLabel(source)}
            </span>
            <span className="font-npc-mono text-[11px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {whenCN(memo.updated_at ?? memo.created_at)}
            </span>
            {memo.audio ? <Fact tone="n" label={clockCN(memo.audio.duration_ms)} /> : null}
            {images > 0 ? (
              <Fact tone="n" label={t('memo.list.fact.images', { n: String(images) })} />
            ) : null}
            {files > 0 ? (
              <Fact tone="n" label={t('memo.list.fact.files', { n: String(files) })} />
            ) : null}
          </p>
        </div>
      </button>
    </li>
  )
}

/** 来源五种：由数据判，不由 AI 编。 */
export type MemoSource = 'text' | 'voice' | 'link' | 'file' | 'lulu'

/** 链接条目判法：正文以 http(s):// 开头（设计稿 SEED 的链接条目就是这个形状）。 */
const URL_LEAD = /^https?:\/\/\S+/

export function memoSource(memo: Schemas['Memo']): MemoSource {
  if (memo.audio) return 'voice'
  if (memo.author === 'ai') return 'lulu'
  if (URL_LEAD.test((memo.body ?? '').trimStart())) return 'link'
  if ((memo.attachments ?? []).length > 0) return 'file'
  return 'text'
}

function sourceLabel(source: MemoSource): string {
  switch (source) {
    case 'voice':
      return t('memo.list.source.voice')
    case 'link':
      return t('memo.list.source.link')
    case 'file':
      return t('memo.list.source.file')
    case 'lulu':
      return t('memo.list.source.lulu')
    default:
      return t('memo.list.source.text')
  }
}

function SourceIcon(props: { source: MemoSource; size: number }) {
  const { source, size } = props
  const stroke = { strokeWidth: 1.8, size, 'aria-hidden': true as const }
  switch (source) {
    case 'voice':
      return <Mic {...stroke} />
    case 'link':
      return <Link2 {...stroke} />
    case 'lulu':
      return <MessageSquare {...stroke} />
    case 'file':
      return <ImageIcon {...stroke} />
    default:
      return <AlignLeft {...stroke} />
  }
}

/** `1 分 08 秒`。真实时长，不估算。 */
export function clockCN(ms: number): string {
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m === 0) return `${s} 秒`
  return `${m} 分 ${String(s).padStart(2, '0')} 秒`
}

/** 当天给「今天 09:22」，昨天给「昨天 17:03」，更早给「8 月 22 日」（跨年带年份）。 */
export function whenCN(iso: string): string {
  const days = daysBetween(iso)
  if (days === 0) return t('memo.when.today', { time: hhmm(iso) })
  if (days === 1) return t('memo.when.yesterday', { time: hhmm(iso) })
  const d = new Date(iso)
  const now = new Date()
  const md = t('memo.when.date', { m: String(d.getMonth() + 1), d: String(d.getDate()) })
  if (d.getFullYear() !== now.getFullYear()) {
    return t('memo.when.full', {
      y: String(d.getFullYear()),
      m: String(d.getMonth() + 1),
      d: String(d.getDate()),
    })
  }
  return md
}

/** 给归档区等次要列表用的时间（不带「今天/昨天」前缀的短日期）。 */
export function whenShort(iso: string): string {
  const d = new Date(iso)
  return t('memo.when.date', { m: String(d.getMonth() + 1), d: String(d.getDate()) })
}
